import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import {spacing} from '@/products/flexible-web-toolkit/styles'
import type {Block, Tersed} from '@/services/kontent-ai/types'
import {CardImage} from '@/products/flexible-web-toolkit/components/card/card-image'
import {CardFooter} from '@/products/flexible-web-toolkit/components/card/card-footer'
import {Card as ClCard} from '@/products/flexible-web-toolkit/components/card/index'
import {
	CardModalTriggerBlock,
	type CardModalTriggerContentItem,
} from '@/products/flexible-web-toolkit/blocks/card-modal-trigger/card-modal-trigger'
import type {CtaButtonContentItem} from '../cta-button'
import {ReusableCTAButtonComponent} from '../cta-button'
import type {RichTextContentItem} from '../rich-text'
import {RichTextBlock} from '../rich-text'
import {Image} from '../../components/image'

export type RichCardContentItem = IContentItem<{
	titleIcon: Elements.AssetsElement
	title: Elements.TextElement
	image: Elements.AssetsElement
	richText: Elements.LinkedItemsElement<RichTextContentItem>
	button: Elements.LinkedItemsElement<
		CtaButtonContentItem | CardModalTriggerContentItem
	>
	snippetSpacingSpacing: Elements.MultipleChoiceElement
}>

export const RichCardBlock: Block<RichCardContentItem> = ({
	block,
	...context
}) => {
	const spacingChoice =
		spacing[
			block.elements.snippetSpacingSpacing[0]
				?.codename as keyof typeof spacing
		]
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not unnecessary, snippetSpacingSpacing may be null
	const componentSpacing = !spacingChoice ? spacing.none : spacingChoice

	const cardImage = block.elements.image[0]
	const cardTitleIcon = block.elements.titleIcon[0]

	return (
		<ClCard
			className={clsx('h-full flex-none p-4', componentSpacing)}
			title={
				cardTitleIcon ? (
					<div className="flex items-center justify-start gap-2 text-left text-lg leading-tight">
						<Image
							alt={cardTitleIcon.description ?? ''}
							className="shrink-0"
							height={cardTitleIcon.height ?? 32}
							src={cardTitleIcon.url}
							width={cardTitleIcon.width ?? 32}
						/>
						<span data-kontent-element-codename="title">
							{block.elements.title}
						</span>
					</div>
				) : (
					<span data-kontent-element-codename="title">
						{block.elements.title}
					</span>
				)
			}
		>
			{cardImage ? (
				<CardImage
					alt={cardImage.description}
					data-kontent-element-codename="image"
					src={cardImage.url}
				/>
			) : null}
			{block.elements.richText[0] ? (
				<RichTextBlock
					block={block.elements.richText[0]}
					{...context}
				/>
			) : null}
			{(() => {
				const button = block.elements.button[0]
				if (!button) return null
				return (
					<CardFooter>
						{button.system.type === 'cta_button' ? (
							<ReusableCTAButtonComponent
								block={button as Tersed<CtaButtonContentItem>}
								className="w-full"
								{...context}
							/>
						) : (
							<CardModalTriggerBlock
								block={
									button as Tersed<CardModalTriggerContentItem>
								}
								{...context}
							/>
						)}
					</CardFooter>
				)
			})()}
		</ClCard>
	)
}
